import React, { useMemo } from 'react'

import { useField } from 'formik'

import { WishAndExpectation, RadioOption, WishesRatingFieldContainerProps } from '../../../ana-toolbox'
import { wishesSettings } from '../../utils/config/wishes'
import { Radio } from '../../utils/shared/Radio'
import { RadioFormControl } from '../../utils/shared/RadioFormControl'
import { RadioFormControlLabel } from '../../utils/shared/RadioFormControlLabel'
import { RadioFormLabel } from '../../utils/shared/RadioFormLabel'
import { RadioGroup } from '../../utils/shared/RadioGroup'
import { Translate } from '../translate/translate'

export type WishesRatingFieldProps = {
  label: string
  name: string
  value?: string
  options: RadioOption[]
  required?: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const WishesRatingFieldBase = ({ name, label, value, options, required, onChange }: WishesRatingFieldProps) => {
  return (
    <RadioFormControl fullWidth>
      <RadioFormLabel required={required}>
        <Translate>{label}</Translate>
      </RadioFormLabel>

      <RadioGroup aria-label={label?.toString()} onChange={onChange} value={value?.toString() || ''}>
        {options.map((option) => (
          <RadioFormControlLabel
            id={`${option.value}-label`}
            key={option.value}
            value={option.value}
            name={name}
            control={
              <Radio
                inputProps={{
                  'aria-labelledby': `${option.value}-label`,
                }}
              />
            }
            label={<Translate>{option.label}</Translate>}
          />
        ))}
      </RadioGroup>
    </RadioFormControl>
  )
}

const addWish = (newWish: WishAndExpectation, wishes: WishAndExpectation[]): WishAndExpectation[] => [
  ...wishes,
  newWish,
]

const updateWish = (wishToUpdate: WishAndExpectation, wishes: WishAndExpectation[]): WishAndExpectation[] =>
  wishes.map((wish) => (wish.key === wishToUpdate.key ? wishToUpdate : wish))

export const WishesRatingField = ({ wishKey, label, required }: WishesRatingFieldContainerProps) => {
  const [props, _, helpers] = useField<WishAndExpectation[]>('wishesAndExpectations')

  const wishes = props.value || []
  const wish = props.value?.find(({ key }) => key === wishKey)

  const wishSettings = useMemo(() => wishesSettings[wishKey], [wishKey])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWish = wishSettings.wishFactory
      ? wishSettings.wishFactory(e.target.name, e.target.value)
      : { key: e.target.name, score: Number(e.target.value) }

    const updatedWishes = wish ? updateWish(newWish, wishes) : addWish(newWish, wishes)

    helpers.setValue(
      wishSettings?.updateDependantWishes ? wishSettings.updateDependantWishes(newWish, updatedWishes) : updatedWishes
    )
  }

  return (
    <WishesRatingFieldBase
      label={label}
      required={required}
      options={wishSettings?.ratingOptions || []}
      onChange={handleChange}
      value={String(wish?.score ?? wish?.flag)}
      name={wishKey}
    />
  )
}
